import { useState } from 'react'
import { ChevronRight, MoreHorizontal } from 'lucide-react'
import { BreadcrumbSubPagesDropdown } from './BreadcrumbSubPagesDropdown'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

const MAX_VISIBLE_ANCESTORS = 2

interface CollapsibleAncestorBreadcrumbsProps {
  ancestors: Array<PageDocument>
  projectId: string
  pages: Array<PageDocument>
  renderLink: (ancestor: PageDocument) => React.ReactNode
  onNavigate: (slug: string) => void
}

export function CollapsibleAncestorBreadcrumbs({
  ancestors,
  projectId,
  pages,
  renderLink,
  onNavigate,
}: CollapsibleAncestorBreadcrumbsProps) {
  const [open, setOpen] = useState(false)

  if (ancestors.length === 0) return null

  const needsCollapsing = ancestors.length > MAX_VISIBLE_ANCESTORS

  if (!needsCollapsing) {
    return (
      <>
        {ancestors.map((ancestor) => (
          <span key={ancestor.id} className="flex items-center gap-1">
            <ChevronRight className="size-2.5 flex-shrink-0" />
            <span className="flex items-center gap-0.5 truncate">
              {renderLink(ancestor)}
              <BreadcrumbSubPagesDropdown
                currentPageId={ancestor.id}
                projectId={projectId}
                pages={pages}
                onNavigate={onNavigate}
              />
            </span>
          </span>
        ))}
      </>
    )
  }

  // Show first ancestor, collapsed middle, last ancestor
  const first = ancestors[0]
  const middle = ancestors.slice(1, -1)
  const last = ancestors[ancestors.length - 1]
  // Guarded above by the length checks, but those do not narrow an index access.
  if (!first || !last) return null

  return (
    <>
      {/* First ancestor */}
      <span className="flex items-center gap-1">
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <span className="flex items-center gap-0.5 truncate">
          {renderLink(first)}
          <BreadcrumbSubPagesDropdown
            currentPageId={first.id}
            projectId={projectId}
            pages={pages}
            onNavigate={onNavigate}
          />
        </span>
      </span>

      {/* Collapsed middle ancestors */}
      <span className="flex items-center gap-1">
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center justify-center rounded hover:bg-accent/50 transition-colors px-1 py-0.5"
              aria-label={`Show ${middle.length} hidden pages`}
            >
              <MoreHorizontal className="size-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto max-w-64 p-1">
            <div className="flex flex-col gap-0.5">
              {middle.map((ancestor) => (
                <button
                  key={ancestor.id}
                  type="button"
                  className="flex items-center gap-1.5 px-2 py-1.5 text-xs rounded hover:bg-accent transition-colors text-left truncate"
                  onClick={() => {
                    onNavigate(ancestor.slug)
                    setOpen(false)
                  }}
                >
                  <span className="truncate">{ancestor.title || 'Untitled'}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </span>

      {/* Last ancestor */}
      <span className="flex items-center gap-1">
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <span className="flex items-center gap-0.5 truncate">
          {renderLink(last)}
          <BreadcrumbSubPagesDropdown
            currentPageId={last.id}
            projectId={projectId}
            pages={pages}
            onNavigate={onNavigate}
          />
        </span>
      </span>
    </>
  )
}
